import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, apiErr } from "../../lib/api";
import { fmtEuro, fmtNum, fmtPct, fmtDate } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Download, Trash2, Loader2, Save, Eye, Copy, Maximize, Sparkles, ClipboardCopy } from "lucide-react";

const STATI = ["Nuova", "Contattata", "Incarico acquisito", "Incarico firmato", "Gestione alleato", "Persa", "Chiusa senza esito"];

const Box = ({ title, children, testid }) => (
  <div className="bg-white rounded-2xl border border-[#DDE5E2] p-5" data-testid={testid}>
    <h3 className="text-sm uppercase tracking-[0.12em] font-semibold text-[#6C8EAD] mb-4">{title}</h3>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-[#EDF2F0] last:border-0 text-sm">
    <span className="text-[#5B6E78]">{label}</span>
    <span className="text-[#2B3A42] font-medium text-right">{value}</span>
  </div>
);

export const SimulazioneDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [sim, setSim] = useState(null);
  const [stato, setStato] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  useEffect(() => {
    api.get(`/admin/simulazioni/${id}`)
      .then((r) => { setSim(r.data); setStato(r.data.stato); setNote(r.data.note_interne || ""); })
      .catch((e) => toast.error(apiErr(e)));
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/simulazioni/${id}`, { stato, note_interne: note });
      toast.success("Modifiche salvate.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/simulazioni/${id}`);
      toast.success("Simulazione eliminata.");
      nav("/admin");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/admin/simulazioni/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${sim.codice}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openPreview = () => {
    setPreviewLoading(true);
    setPreviewUrl(`${API}/admin/simulazioni/${id}/pdf?inline=true`);
    setPreviewOpen(true);
    setTimeout(() => setPreviewLoading(false), 2500);
  };

  const previewRef = useRef(null);
  const toggleFullscreen = () => {
    const el = previewRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else if (el.requestFullscreen) el.requestFullscreen();
  };

  const numStr = (n) => {
    if (n === null || n === undefined || n === "") return "";
    return Number.isInteger(n) ? String(n) : String(n).replace(".", ",");
  };

  const duplica = () => {
    const az2 = sim.azienda;
    const years = {};
    [2021, 2022, 2023].forEach((yr) => {
      const r = sim.risultati.find((x) => x.anno === yr);
      years[yr] = r ? {
        fatturato: numStr(r.fatturato),
        costo_personale: numStr(r.costo_personale),
        dipendenti: numStr(r.dipendenti),
        persone_progetto: numStr(r.persone_progetto),
        costo_amministratore: numStr(r.costo_amministratore),
        costi_professionisti: numStr(r.costi_professionisti),
      } : { fatturato: "", costo_personale: "", dipendenti: "", persone_progetto: "", costo_amministratore: "", costi_professionisti: "" };
    });
    const prefill = {
      company: {
        referente: az2.referente, ragione_sociale: az2.ragione_sociale, partita_iva: az2.partita_iva,
        email: az2.email, cellulare: (az2.cellulare || "").replace(/^\+39\s*/, ""),
        settore: az2.settore, tipo_utente: az2.tipo_utente, ruolo: az2.ruolo,
        ha_revisore: az2.ha_revisore, ha_durc: az2.ha_durc,
      },
      years,
    };
    nav("/", { state: { prefill } });
  };

  const generaAi = async (tipo) => {
    setAiLoading(true);
    setAiText("");
    setAiError("");
    try {
      const r = await api.post(`/admin/simulazioni/${id}/ai`, { tipo }, { timeout: 90000 });
      setAiText(r.data.testo);
    } catch (e) {
      const msg = e.code === "ECONNABORTED"
        ? "Il servizio AI ci sta mettendo troppo, riprova."
        : apiErr(e);
      setAiError(msg);
      toast.error(msg);
    }
    finally { setAiLoading(false); }
  };

  const copyAi = () => {
    navigator.clipboard.writeText(aiText);
    toast.success("Testo copiato.");
  };

  if (!sim) return <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-[#6C8EAD]" /></div>;

  const az = sim.azienda;

  return (
    <div data-testid="admin-simulation-detail-card">
      <button onClick={() => nav("/admin")} className="flex items-center gap-2 text-sm text-[#5B6E78] hover:text-[#2B3A42] mb-4">
        <ArrowLeft className="h-4 w-4" /> Torna all'elenco
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#2B3A42]">{az.ragione_sociale}</h1>
          <p className="text-sm text-[#5B6E78]">Codice {sim.codice} · {fmtDate(sim.created_at)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="detail-preview-pdf" onClick={openPreview} variant="outline"
            className="gap-2 rounded-full border-[#DDE5E2] text-[#4F708C] hover:bg-[#EDF2F0]">
            <Eye className="h-4 w-4" /> Anteprima PDF
          </Button>
          <Button data-testid="detail-download-pdf" onClick={downloadPdf} className="gap-2 rounded-full bg-[#6C8EAD] hover:bg-[#4F708C] text-white">
            <Download className="h-4 w-4" /> Scarica PDF
          </Button>
          <Button data-testid="detail-duplicate-btn" onClick={duplica} variant="outline"
            className="gap-2 rounded-full border-[#DDE5E2] text-[#4F683E] hover:bg-[#EEF4EB]">
            <Copy className="h-4 w-4" /> Duplica
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="detail-delete-btn" variant="outline" className="gap-2 rounded-full border-[#F8D7DA] text-[#9E4756] hover:bg-[#FDF2F4]">
                <Trash2 className="h-4 w-4" /> Elimina
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare la simulazione?</AlertDialogTitle>
                <AlertDialogDescription>L'operazione è irreversibile. Tutti i dati di questa simulazione verranno rimossi.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction data-testid="detail-delete-confirm" onClick={remove} className="bg-[#9E4756] hover:bg-[#83394680]">Elimina</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Box title="Dati azienda e referente">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <Row label="Ragione sociale" value={az.ragione_sociale} />
              <Row label="P.IVA" value={az.partita_iva} />
              <Row label="Referente" value={az.referente} />
              <Row label="Ruolo" value={az.ruolo} />
              <Row label="Email" value={az.email} />
              <Row label="Cellulare" value={az.cellulare || "—"} />
              <Row label="Settore" value={az.settore} />
              <Row label="Tipo utente" value={az.tipo_utente} />
              <Row label="Revisore / Collegio" value={az.ha_revisore ? "Sì" : "No"} />
              <Row label="DURC valido" value={az.ha_durc ? "Sì" : "No"} />
            </div>
          </Box>

          <Box title="Provenienza (solo admin)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
              <Row label="Canale" value={sim.canale_nome} />
              <Row label="utm_source" value={sim.utm_source || "—"} />
              <Row label="utm_medium" value={sim.utm_medium || "—"} />
              <Row label="utm_campaign" value={sim.utm_campaign || "—"} />
            </div>
          </Box>

          <Box title="Risultati per anno (dati riservati admin)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#5B6E78] border-b border-[#DDE5E2]">
                    <th className="py-2 pr-3">Anno</th>
                    <th className="py-2 px-3 text-right">Costo personale</th>
                    <th className="py-2 px-3 text-center">Pers/Dip</th>
                    <th className="py-2 px-3 text-right">Quota</th>
                    <th className="py-2 px-3 text-center">Coeff.</th>
                    <th className="py-2 px-3 text-right">Base agevolabile</th>
                    <th className="py-2 px-3 text-center">Aliquota</th>
                    <th className="py-2 px-3 text-right">Credito</th>
                    <th className="py-2 px-3 text-right">Incidenza</th>
                  </tr>
                </thead>
                <tbody>
                  {sim.risultati.map((r) => (
                    <tr key={r.anno} className="border-b border-[#EDF2F0]">
                      <td className="py-2 pr-3 font-medium">{r.anno}</td>
                      <td className="py-2 px-3 text-right tnum">{fmtEuro(r.costo_personale)}</td>
                      <td className="py-2 px-3 text-center tnum">{r.persone_progetto}/{r.dipendenti}</td>
                      <td className="py-2 px-3 text-right tnum">{fmtNum(r.quota * 100, 2)}%</td>
                      <td className="py-2 px-3 text-center tnum">{fmtNum(r.coefficiente * 100, 0)}%</td>
                      <td className="py-2 px-3 text-right tnum">{fmtEuro(r.base_agevolabile)}</td>
                      <td className="py-2 px-3 text-center tnum">{fmtNum(r.aliquota * 100, 0)}%</td>
                      <td className="py-2 px-3 text-right tnum font-medium">{fmtEuro(r.credito)}</td>
                      <td className="py-2 px-3 text-right tnum">{fmtPct(r.incidenza, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between mt-4 pt-3 border-t border-[#DDE5E2]">
              <span className="text-sm font-medium text-[#2B3A42]">Credito totale</span>
              <span className="text-lg font-semibold text-[#4F683E] tnum">{fmtEuro(sim.credito_totale)}</span>
            </div>
          </Box>

          {sim.mostra_altri_costi && (
            <Box title="Altri costi rilevati (non inclusi nella stima)">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8">
                <Row label="Amministratore" value={fmtEuro(sim.altri_costi_admin_totale)} />
                <Row label="Professionisti" value={fmtEuro(sim.altri_costi_prof_totale)} />
                <Row label="Totale" value={fmtEuro(sim.altri_costi_totale)} />
              </div>
            </Box>
          )}
        </div>

        <div className="space-y-5">
          <Box title="Gestione lead">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Stato</Label>
                <Select value={stato} onValueChange={setStato}>
                  <SelectTrigger data-testid="detail-stato-select" className="h-10 border-[#DDE5E2]"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Note interne</Label>
                <Textarea data-testid="detail-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                  rows={6} className="border-[#DDE5E2] resize-none" placeholder="Annotazioni riservate..." />
              </div>
              <Button data-testid="detail-save-btn" onClick={save} disabled={saving}
                className="w-full gap-2 rounded-full bg-[#9CAF88] hover:bg-[#7C8F69] text-white">
                <Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva modifiche"}
              </Button>
            </div>
          </Box>
          <Box title="Livello di preparazione">
            <p className="text-2xl font-semibold text-[#2B3A42]">{sim.livello_preparazione}</p>
          </Box>

          <Box title="Assistente AI (Claude)" testid="ai-assistant-box">
            <p className="text-xs text-[#5B6E78] mb-3">
              Genera testi di supporto per uso interno. Sono bozze da rivedere prima dell'uso.
            </p>
            <div className="flex flex-col gap-2">
              <Button data-testid="ai-riepilogo-btn" onClick={() => generaAi("riepilogo")} disabled={aiLoading}
                variant="outline" className="justify-start gap-2 rounded-xl border-[#DDE5E2] text-[#2B3A42] hover:bg-[#EDF2F0]">
                <Sparkles className="h-4 w-4 text-[#6C8EAD]" /> Riepilogo sintetico
              </Button>
              <Button data-testid="ai-email-btn" onClick={() => generaAi("email")} disabled={aiLoading}
                variant="outline" className="justify-start gap-2 rounded-xl border-[#DDE5E2] text-[#2B3A42] hover:bg-[#EDF2F0]">
                <Sparkles className="h-4 w-4 text-[#9CAF88]" /> Bozza email
              </Button>
              <Button data-testid="ai-analisi-btn" onClick={() => generaAi("analisi")} disabled={aiLoading}
                variant="outline" className="justify-start gap-2 rounded-xl border-[#DDE5E2] text-[#2B3A42] hover:bg-[#EDF2F0]">
                <Sparkles className="h-4 w-4 text-[#4F708C]" /> Analisi tecnica
              </Button>
            </div>
            {aiLoading && (
              <div className="flex items-center gap-2 text-sm text-[#5B6E78] mt-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Generazione in corso...
              </div>
            )}
            {aiError && !aiLoading && (
              <p data-testid="ai-error" className="text-sm text-[#9E4756] mt-4">{aiError}</p>
            )}
            {aiText && !aiLoading && (
              <div className="mt-4">
                <Textarea data-testid="ai-output" value={aiText} readOnly rows={12}
                  className="border-[#DDE5E2] text-sm resize-none whitespace-pre-wrap" />
                <Button data-testid="ai-copy-btn" onClick={copyAi} variant="outline"
                  className="mt-2 gap-2 rounded-full border-[#DDE5E2] text-[#4F708C] hover:bg-[#EDF2F0]">
                  <ClipboardCopy className="h-4 w-4" /> Copia testo
                </Button>
              </div>
            )}
          </Box>
        </div>
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 gap-0" data-testid="pdf-preview-dialog">
          <DialogHeader className="px-5 py-4 border-b border-[#DDE5E2]">
            <DialogTitle className="text-[#2B3A42]">Anteprima PDF — {sim.codice}</DialogTitle>
          </DialogHeader>
          <div ref={previewRef} className="bg-[#EDF2F0] relative">
            {previewLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <Loader2 className="h-6 w-6 animate-spin text-[#6C8EAD]" />
              </div>
            )}
            {previewUrl && (
              <iframe
                data-testid="pdf-preview-frame"
                title="Anteprima PDF"
                src={`${previewUrl}#toolbar=1&view=FitH`}
                onLoad={() => setPreviewLoading(false)}
                className="w-full h-[70vh] border-0"
              />
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#DDE5E2]">
            <Button variant="outline" onClick={toggleFullscreen} data-testid="preview-fullscreen-btn"
              className="gap-2 rounded-full border-[#DDE5E2] text-[#4F708C] hover:bg-[#EDF2F0]">
              <Maximize className="h-4 w-4" /> Schermo intero
            </Button>
            <Button onClick={downloadPdf} className="gap-2 rounded-full bg-[#6C8EAD] hover:bg-[#4F708C] text-white">
              <Download className="h-4 w-4" /> Scarica PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
